// components/FormContainer.tsx
// components/FormContainer.tsx
import type React from 'react';
import { useEffect } from 'react';

import {
  Form,
  Formik,
  type FormikConfig,
  type FormikErrors,
  type FormikProps,
} from 'formik';

interface FormContainerProps<T> {
  initialValues: T;
  onSubmit: FormikConfig<T>['onSubmit'];
  children: (props: FormikProps<T>) => React.ReactNode;
  validationSchema?: FormikConfig<T>['validationSchema'];
  serverErrors?: FormikErrors<T>;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  enableReinitialize?: boolean;
}

function FormInner<T>({
  formikProps,
  serverErrors,
  children,
}: {
  formikProps: FormikProps<T>;
  serverErrors?: FormikErrors<T>;
  children: (props: FormikProps<T>) => React.ReactNode;
}) {
  useEffect(() => {
    if (serverErrors) {
      formikProps.setErrors(serverErrors);
    }
  }, [serverErrors, formikProps]);

  return <Form>{children(formikProps)}</Form>;
}

export function FormContainer<T extends object>({
  initialValues,
  onSubmit,
  children,
  validationSchema,
  serverErrors,
  validateOnBlur = true,
  validateOnChange = true,
  enableReinitialize = true,
}: FormContainerProps<T>) {
  return (
    <Formik<T>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnBlur={validateOnBlur}
      validateOnChange={validateOnChange}
      enableReinitialize={enableReinitialize}
    >
      {(formikProps) => (
        <FormInner<T> formikProps={formikProps} serverErrors={serverErrors}>
          {children}
        </FormInner>
      )}
    </Formik>
  );
}
